import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import GatewayHeader from '../_gateway/GatewayHeader';
import GatewayFooter from '../_gateway/GatewayFooter';
import '../_gateway/gateway.css';

import { requestLiveDemo, resetRequestDemo, selectStatus } from '_redux/slices/website/request-demo';

const INTEREST_OPTIONS = [
  'Compliance & Risk',
  'Operational Efficiency',
  'Passenger Experience',
  'Service Planning & Realtime Execution',
  'Workforce Experience',
  'Technical Integration & Data Systems',
];

const fieldLabelStyle = { display: 'block', margin: '0 0 8px', fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#1c2b4a' };
const inputStyle = { width: '100%', minHeight: 48, padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(32,138,239,0.3)', background: '#fff', fontSize: 16, fontFamily: 'var(--gw-font-sans)', color: '#1c2b4a' };

const initialForm = { name: '', email: '', phone: '', title: '', company: '', interests: [] };

const RequestDemo = () => {
  const dispatch = useDispatch();
  const status = useSelector(selectStatus);
  const [form, setForm] = useState(initialForm);
  const [validationError, setValidationError] = useState('');
  const successRef = useRef(null);

  useEffect(() => () => { dispatch(resetRequestDemo()); }, [dispatch]);

  useEffect(() => {
    if (status !== 'succeeded') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'generate_lead', form_id: 'book-a-demo' });
    if (successRef.current) successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [status]);

  const updateField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleInterest = (value) => {
    setForm((f) => {
      const has = f.interests.includes(value);
      return { ...f, interests: has ? f.interests.filter((v) => v !== value) : [...f.interests, value] };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');
    if (!form.name.trim() || !form.email.trim() || !form.title.trim() || !form.company.trim()) {
      setValidationError('Please fill in your name, email, title and company.');
      return;
    }
    dispatch(requestLiveDemo({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      title: form.title.trim(),
      company: form.company.trim(),
      interests: form.interests,
    }));
  };

  const submitting = status === 'loading';
  const succeeded = status === 'succeeded';
  const errorMessage = validationError
    || (status === 'failed' ? "Something went wrong sending your request — please try again, or email demorequest@prmassist.com directly." : '');

  return (
    <div className="gw-page">
      <GatewayHeader />

      <main id="main">
        <section style={{ maxWidth: 900, margin: '0 auto', padding: '72px 32px 0', textAlign: 'center' }}>
          <p style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: '0 0 16px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#1670c8' }}>
            <span aria-hidden="true">→</span> Book a demo
          </p>
          <h1 style={{ margin: 0, fontSize: 'clamp(30px, 3.6vw, 48px)', lineHeight: 1.04, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>
            See PRM Assist in action.
          </h1>
          <p style={{ margin: '22px auto 0', maxWidth: '34em', fontSize: 17, lineHeight: 1.6, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
            Tell us a little about you and your operation, and we&apos;ll be in touch to schedule a walkthrough of the platform tailored to your airport, airline or service provider team.
          </p>
        </section>

        <section aria-label="Demo request form" style={{ maxWidth: 640, margin: '44px auto 0', padding: '0 32px 96px' }}>
          {!succeeded && (
            <form onSubmit={handleSubmit} noValidate style={{ borderRadius: 28, background: '#f4f8fe', border: '1px solid rgba(32,138,239,0.14)', boxShadow: '0 10px 30px rgba(28,43,74,0.07)', padding: '40px 36px' }}>
              <div style={{ marginBottom: 22 }}>
                <label htmlFor="df-name" style={fieldLabelStyle}>Name <span aria-hidden="true" style={{ color: '#208aef' }}>*</span></label>
                <input id="df-name" type="text" required autoComplete="name" value={form.name} onChange={updateField('name')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 22 }}>
                <label htmlFor="df-email" style={fieldLabelStyle}>Email <span aria-hidden="true" style={{ color: '#208aef' }}>*</span></label>
                <input id="df-email" type="email" required autoComplete="email" value={form.email} onChange={updateField('email')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 22 }}>
                <label htmlFor="df-phone" style={fieldLabelStyle}>Phone <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280' }}>(optional)</span></label>
                <input id="df-phone" type="tel" autoComplete="tel" value={form.phone} onChange={updateField('phone')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 22 }}>
                <label htmlFor="df-title" style={fieldLabelStyle}>Title <span aria-hidden="true" style={{ color: '#208aef' }}>*</span></label>
                <input id="df-title" type="text" required autoComplete="organization-title" value={form.title} onChange={updateField('title')} style={inputStyle} />
              </div>

              <div style={{ marginBottom: 26 }}>
                <label htmlFor="df-company" style={fieldLabelStyle}>Company <span aria-hidden="true" style={{ color: '#208aef' }}>*</span></label>
                <input id="df-company" type="text" required autoComplete="organization" value={form.company} onChange={updateField('company')} style={inputStyle} />
              </div>

              <fieldset style={{ margin: '0 0 30px', padding: 0, border: 0 }}>
                <legend style={{ margin: '0 0 12px', padding: 0, fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#1c2b4a' }}>Areas of interest</legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {INTEREST_OPTIONS.map((label) => (
                    <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>
                      <input
                        type="checkbox"
                        checked={form.interests.includes(label)}
                        onChange={() => toggleInterest(label)}
                        style={{ width: 18, height: 18, accentColor: '#208aef' }}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              {errorMessage && (
                <div role="alert" style={{ margin: '0 0 18px', padding: '12px 16px', borderRadius: 10, background: '#fdecea', color: '#c0392b', fontSize: 14, fontFamily: 'var(--gw-font-sans)' }}>
                  {errorMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{ width: '100%', minHeight: 52, border: 0, borderRadius: 12, background: '#1c2b4a', color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}
              >
                {submitting ? 'Sending…' : 'Request my demo'}
              </button>
            </form>
          )}

          {succeeded && (
            <div ref={successRef} style={{ borderRadius: 28, background: '#f4f8fe', border: '1px solid rgba(32,138,239,0.14)', boxShadow: '0 10px 30px rgba(28,43,74,0.07)', padding: '52px 36px', textAlign: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#1c2b4a' }}>Thanks — request received.</h2>
              <p style={{ margin: '16px 0 0', fontSize: 16, lineHeight: 1.6, color: '#374151', fontFamily: 'var(--gw-font-sans)' }}>Someone from our team will be in touch within one business day to schedule your demo.</p>
            </div>
          )}

          <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 14, color: '#9ca3af', fontFamily: 'var(--gw-font-sans)' }}>
            Prefer email? Reach us directly at <a href="mailto:demorequest@prmassist.com">demorequest@prmassist.com</a>.
          </p>
        </section>
      </main>

      <GatewayFooter />
    </div>
  );
};

export default RequestDemo;
